import React, { useCallback, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { Search as SearchIcon, CircleX as HighlightOffIcon, Plus as AddIcon } from 'lucide-react';

import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { useAppDispatch, useAppSelector } from 'src/store';
import Input from 'src/shared/components/input/Input';

import styles from './UserManagementFilters.module.scss';
import {
    UserManagementListFilterState,
    resetUserManagementListFilterState,
    updateGeneralUiState,
    updaterUserManagementListFilterState,
} from '../../state/userManagementSlice';
import { getUserManagementListFilterState } from '../../state/userManagementSelector';

const UserManagementFilters = () => {
    const userManagementListFilterState = useAppSelector(getUserManagementListFilterState);
    const dispatch = useAppDispatch();
    const [userSearchText, setUserSearchText] = React.useState(userManagementListFilterState.userEmailSearchText);

    useEffect(() => {
        setUserSearchText(userManagementListFilterState.userEmailSearchText);
    }, [userManagementListFilterState.userEmailSearchText]);

    // Stop the invocation of the debounced function after unmounting
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, []);

    const handleResetFilters = () => {
        debouncedSearch.cancel();
        dispatch(resetUserManagementListFilterState());
        setUserSearchText('');
    };

    const onFilterChange = (fragment: Partial<UserManagementListFilterState>) => {
        dispatch(
            updaterUserManagementListFilterState({
                ...fragment,
            })
        );
    };

    const debouncedSearch = useCallback(
        debounce((textValue: string) => {
            onFilterChange({ userEmailSearchText: textValue, currentPageNumber: 1 });
        }, 500),
        []
    );

    const onUserSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserSearchText(event.target.value);

        // debounce the api call by 500ms
        debouncedSearch(event.target.value);
    };

    const openCreateUserModal = () => {
        dispatch(
            updateGeneralUiState({
                shouldShowNewUserModal: true,
            })
        );
    };

    return (
        <div className={styles.userManagementFilters} data-testid='user-management-filters'>
            <div className={styles.filters}>
                <div className={styles.fieldContainer}>
                    <label>User search</label>
                    <div className={styles.searchInputContainer} style={{ width: '300px' }}>
                        <span className={styles.searchIcon}>
                            <SearchIcon />
                        </span>
                        <Input
                            className={styles.searchInput}
                            placeholder='Type an email address'
                            value={userSearchText}
                            onChange={onUserSearchChange}
                            data-testid='user-management-user-search-input'
                        />
                    </div>
                </div>

                <SecondaryButton className={styles.buttonReset} onClick={handleResetFilters} data-testid='user-management-reset-filters'>
                    <span>Reset filters</span> <HighlightOffIcon />
                </SecondaryButton>
            </div>

            <PrimaryButton
                className={styles.createUserButton}
                onClick={openCreateUserModal}
                data-testid='user-management-open-add-user-modal-filters'
            >
                Add user <AddIcon />
            </PrimaryButton>
        </div>
    );
};

export default UserManagementFilters;
